import { PausedPill } from '@/shared/components/PublishingPause';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { HOME_ROUTE, ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { NotificationsMenu } from './NotificationsMenu';
import { UserMenu } from './UserMenu';
import { BrandMark } from '@/shared/components/BrandMark';
import { useCanWrite } from '@/shared/hooks/useCanWrite';

interface TopbarProps {
  /** A white-label workspace's logo, shown in place of the UniPostudio mark. */
  brandLogoUrl?: string | null;
  onMenuToggle: () => void;
  /** Whether the drawer the menu button opens is open (aria-expanded). */
  menuOpen?: boolean;
  onNewPost: () => void;
  onCommandPalette?: () => void;
}

/**
 * Exactly five things: workspace switcher, search / command palette, the
 * Compose pill, notifications, and the account avatar. Compose is chrome, not
 * a page CTA (DS03): an aubergine outline pill, so each page's own solid
 * primary stands alone, and absent on the composer itself. The menu
 * button only exists on tablets, where the sidebar is a drawer; on phones
 * (blueprint S90) the bar is brand pill, bell and account only: search and
 * Compose live in the bottom tab bar, and More opens the drawer.
 */
export function Topbar({ brandLogoUrl = null, onMenuToggle, menuOpen = false, onNewPost, onCommandPalette }: TopbarProps) {
  // Viewers and clients write no posts: no Compose (PC-26).
  const { canWrite } = useCanWrite();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const composing = pathname.startsWith(ROUTES.COMPOSER);
  const { t } = useTranslation();

  return (
    <header className="fixed top-0 inset-x-0 z-50 h-[64px] bg-[var(--color-surface)] border-b border-[var(--line)] flex items-center justify-between gap-2 sm:gap-3 px-2 sm:px-4 lg:px-6">
      <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
        <button
          type="button"
          className="hidden md:flex lg:hidden items-center justify-center w-11 h-11 rounded-lg bg-transparent border-none cursor-pointer hover:bg-[var(--color-surface-3)] shrink-0"
          onClick={onMenuToggle}
          aria-label={t('topbar.openMenu')}
          aria-expanded={menuOpen}
          aria-controls="app-sidebar"
        >
          <Menu size={20} className="text-[var(--ink)]" aria-hidden="true" />
        </button>

        {brandLogoUrl ? (
          <button
            type="button"
            onClick={() => navigate(HOME_ROUTE)}
            className="hidden sm:flex h-11 max-w-[160px] px-1 items-center bg-transparent border-none cursor-pointer shrink-0"
            aria-label={t('topbar.home')}
          >
            <img src={brandLogoUrl} alt="" className="max-h-8 max-w-full object-contain" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate(HOME_ROUTE)}
            className="hidden sm:flex w-11 h-11 items-center justify-center bg-transparent border-none cursor-pointer shrink-0 p-0 rounded-[10px]"
            aria-label={t('topbar.home')}
          >
            <BrandMark size={36} />
          </button>
        )}

        <WorkspaceSwitcher />
        {/* Publishing paused for the brand (Task 1.5). */}
        <PausedPill />

        <button
          type="button"
          onClick={onCommandPalette}
          className="hidden md:flex items-center gap-2 ms-2 px-3 h-10 rounded-[10px] border border-[var(--color-ai-line)] bg-[var(--color-ai-soft)] text-[var(--color-ai-text)] cursor-pointer hover:border-[var(--color-ai)] min-w-[240px] max-w-[420px] flex-1"
          aria-label={t('topbar.askShortcut')}
          data-testid="topbar-ask"
        >
          <Icons.sparkles size={16} className="shrink-0" aria-hidden="true" />
          <span className="text-ui flex-1 text-start truncate">{t('topbar.ask')}</span>
          <kbd className="hidden lg:inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--line-2)] text-[10px] font-mono text-[var(--ink-3)] bg-[var(--color-surface)]">
            Ctrl K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {/* Tablets keep a search icon; phones reach the palette through More. */}
        <button
          type="button"
          onClick={onCommandPalette}
          className="hidden sm:flex md:hidden w-11 h-11 rounded-full bg-transparent border-none cursor-pointer items-center justify-center hover:bg-[var(--color-surface-3)]"
          aria-label={t('topbar.searchShortcut')}
        >
          <Icons.search size={20} className="text-[var(--ink-2)]" strokeWidth={1.5} aria-hidden="true" />
        </button>

        {/* An outline pill, never the screen's solid primary. On phones the raised
            Compose in the bottom nav is the only one; on the composer there is none. */}
        {canWrite && !composing && (
          <button
            type="button"
            onClick={onNewPost}
            className="btn hidden sm:inline-flex h-10 rounded-full border-[var(--color-accent)] bg-[var(--color-surface)] text-[var(--color-accent-text)] font-semibold hover:bg-[var(--color-accent-soft)]"
          >
            <Icons.add size={16} aria-hidden="true" />
            {t('nav.compose')}
          </button>
        )}

        <NotificationsMenu />
        <UserMenu />
      </div>
    </header>
  );
}
